/**
 * @file components/form-templates/FormTemplateEditor.tsx
 * @description 新增/編輯表單 Modal 內容（2026-09-25 回報序34–42）。
 *
 * 區塊：
 *  1. 基本資料：表單代碼（序40：可自由修改，系統以內部識別鍵配對）/ 表單名稱 / 說明 / 方案 /
 *     適用驗證類型（序37：改用系統「驗證類型」字典）/ 適用驗證類別 / 預設勾選 / 啟用。
 *  2. 當前版本：版本（Rev）與版本日期（序39：取表單本身日期，可手填；不再用系統日期），
 *     顯示原始檔名與檔案上傳日期。與基本資料一起「儲存」（序37：舊版版本號從未送出致更新失敗）。
 *  3. 上傳新版本：.docx / .xlsx（序36），選檔後自動從檔名解析 Rev/日期，可修改後「上傳並識別」。
 *  4. 版本紀錄 + 校對紀錄 + 校對窗口入口（序42）。
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { RcFile } from 'antd/es/upload';
import dayjs, { type Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, FileCheck2, Upload as UploadIcon } from 'lucide-react';
import {
  VERIFICATION_SCHEMES,
  expandLegacyTaskTypes,
  formatRevisionLabel,
  parseRevisionFromFilename,
} from '@zhcb/shared';
import { message } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';
import { uploadFile } from '@/lib/file-upload';
import { previewFile } from '@/lib/file-preview';
import { StatusPill } from '@/components/shell';
import { ProofreadDrawer } from '@/components/form-templates/ProofreadDrawer';
import type {
  FormTemplateDetailResponse,
  FormTemplateListItem,
  FormTemplateVersionDto,
  RecognizeResponse,
} from '@/components/form-templates/types';
import { color, text } from '@/styles/tokens';

/** 適用驗證類別常用標籤（與後端 canonicalCategoryTags 規則對應） */
const CATEGORY_TAG_SUGGESTIONS = [
  '通用',
  '作物',
  '加工',
  '分裝',
  '流通',
  '蜂蜜',
  '集團',
  'TGAP PLUS',
  '履歷轉換',
];

/** 可上傳的表單檔案副檔名 */
const ACCEPT_EXT = ['docx', 'xlsx', 'xlsm'];

/** 表單值型別 */
interface FormValues {
  code: string;
  name: string;
  description?: string | null;
  scheme?: 'TOP' | 'TAP' | 'ECO' | null;
  applicableSchemes?: string[];
  applicableTaskTypes?: string[];
  applicableCategories?: string[];
  isDefault?: boolean;
  isActive?: boolean;
  revisionLabel?: string | null;
  revisionDate?: Dayjs | null;
}

interface Props {
  /** 編輯中的表單；null = 新增 */
  item: FormTemplateListItem | null;
  /** 新增成功後回呼（父層切換為編輯模式，方便接著上傳檔案） */
  onCreated: (created: FormTemplateListItem) => void;
  /** 關閉 Modal */
  onClose: () => void;
}

/** 日期字串 → 顯示 */
function fmtDate(v: string | null | undefined): string {
  return v ? dayjs(v).format('YYYY-MM-DD') : '—';
}

/** 新增/編輯表單內容 */
export function FormTemplateEditor({ item, onCreated, onClose }: Props) {
  const qc = useQueryClient();
  const [form] = Form.useForm<FormValues>();

  /**
   * 字典選項（序37：適用驗證類型改用系統「驗證類型」字典）。
   * 走 /api/form-templates/meta/options（form-template.manage），管理組也讀得到。
   */
  const options = useQuery({
    queryKey: ['form-template-options'],
    queryFn: () =>
      apiClient.get<{ verificationTypes: string[]; verificationCategories: string[] }>(
        '/api/form-templates/meta/options',
      ),
    staleTime: 5 * 60 * 1000,
  });
  const typeNames = useMemo(() => options.data?.verificationTypes ?? [], [options.data]);

  /** 詳情（版本 + 校對紀錄）：僅編輯模式 */
  const detail = useQuery({
    queryKey: ['form-template-detail', item?.id],
    queryFn: () => apiClient.get<FormTemplateDetailResponse>(`/api/form-templates/${item!.id}`),
    enabled: item !== null,
  });
  const versions = useMemo(() => detail.data?.versions ?? [], [detail.data]);
  const proofreads = useMemo(() => detail.data?.proofreads ?? [], [detail.data]);

  /** 當前版本：以詳情為準（上傳新版本後列表項 item 仍是舊快照） */
  const currentVersionId = detail.data?.template.currentVersionId ?? item?.currentVersionId ?? null;
  const currentVer = versions.find((v) => v.id === currentVersionId) ?? null;

  /** 表單初值：舊任務類型代碼展開為字典名稱 */
  const initialValues = useMemo<FormValues>(() => {
    if (!item) return { code: '', name: '', isActive: true, isDefault: false };
    const revisionLabel = currentVer ? currentVer.revisionLabel : item.revisionLabel;
    const revisionDate = currentVer ? currentVer.revisionDate : item.revisionDate;
    return {
      code: item.code,
      name: item.name,
      description: item.description,
      scheme: item.scheme,
      applicableSchemes: item.applicableSchemes ?? [],
      applicableTaskTypes: expandLegacyTaskTypes(item.applicableTaskTypes, typeNames),
      applicableCategories: item.applicableCategories ?? [],
      isDefault: item.isDefault,
      isActive: item.isActive,
      revisionLabel,
      revisionDate: revisionDate ? dayjs(revisionDate) : null,
    };
  }, [item, typeNames, currentVer]);

  /**
   * 字典（驗證類型名稱）晚於表單掛載才載入時，僅就地補正「適用驗證類型」欄的舊代碼展開值；
   * 不以 typeNames 作為 Form key 重掛，否則使用者已輸入的其他欄位會被 initialValues 覆蓋清空。
   * 使用者已動過該欄則不覆蓋。
   */
  useEffect(() => {
    if (!item || typeNames.length === 0) return;
    if (form.isFieldTouched('applicableTaskTypes')) return;
    form.setFieldsValue({ applicableTaskTypes: initialValues.applicableTaskTypes });
  }, [form, item, typeNames, initialValues.applicableTaskTypes]);

  /** 驗證類型選項（字典 + 既有但字典已無的值） */
  const typeOptions = useMemo(() => {
    const names = new Set(typeNames);
    for (const v of initialValues.applicableTaskTypes ?? []) names.add(v);
    return [...names].map((n) => ({ value: n, label: n }));
  }, [typeNames, initialValues.applicableTaskTypes]);

  /** 驗證類別選項（常用標籤 + 字典名稱） */
  const categoryOptions = useMemo(() => {
    const names = new Set(CATEGORY_TAG_SUGGESTIONS);
    for (const c of options.data?.verificationCategories ?? []) names.add(c);
    return [...names].map((n) => ({ value: n, label: n }));
  }, [options.data]);

  /** 失效列表與詳情 */
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['form-templates'] });
    if (item) void qc.invalidateQueries({ queryKey: ['form-template-detail', item.id] });
  };

  /** 表單值 → API body */
  const toBody = (v: FormValues) => ({
    code: v.code,
    name: v.name,
    description: v.description ?? null,
    scheme: v.scheme ?? null,
    applicableSchemes: v.applicableSchemes ?? [],
    applicableTaskTypes: v.applicableTaskTypes ?? [],
    applicableCategories: v.applicableCategories ?? [],
    isDefault: !!v.isDefault,
    isActive: v.isActive !== false,
  });

  /** 新增 */
  const create = useMutation({
    mutationFn: (v: FormValues) =>
      apiClient.post<{ template: FormTemplateListItem }>('/api/form-templates', toBody(v)),
    onSuccess: (res) => {
      message.success('表單已建立，可接著上傳表單檔案（.docx / .xlsx）建立版本');
      invalidate();
      onCreated({
        ...res.template,
        currentVersion: null,
        revisionLabel: null,
        revisionDate: null,
        sourceFileName: null,
        fileUploadedAt: null,
        displayName: res.template.name,
        proofreadStatus: null,
        systemUsage: null,
      });
    },
    onError: (err) => {
      console.error('create form-template failed', err);
      message.error(err instanceof Error ? err.message : '表單建立失敗');
    },
  });

  /** 更新（含當前版本的版本標示/日期） */
  const update = useMutation({
    mutationFn: (v: FormValues) =>
      apiClient.patch(`/api/form-templates/${item!.id}`, {
        ...toBody(v),
        ...(currentVersionId
          ? {
              revisionLabel: v.revisionLabel?.trim() || null,
              revisionDate: v.revisionDate ? v.revisionDate.format('YYYY-MM-DD') : null,
            }
          : {}),
      }),
    onSuccess: () => {
      message.success('表單已更新');
      invalidate();
      onClose();
    },
    onError: (err) => {
      console.error('update form-template failed', err);
      message.error(err instanceof Error ? err.message : '表單更新失敗');
    },
  });

  // ─── 上傳新版本 ─────────────────────────────────────────────────────────
  const [pendingFile, setPendingFile] = useState<RcFile | null>(null);
  const [uploadRev, setUploadRev] = useState<string>('');
  const [uploadDate, setUploadDate] = useState<Dayjs | null>(null);

  /** 選檔：檢查格式並自動從檔名解析版本/日期（序39） */
  const onPickFile = (file: RcFile) => {
    const ext = (/\.([A-Za-z0-9]+)$/.exec(file.name)?.[1] ?? '').toLowerCase();
    if (ext === 'doc' || ext === 'xls') {
      message.error(
        '不支援 Word 97-2003（.doc）/ Excel 97-2003（.xls）舊格式，請於 Word / Excel「另存新檔」為 .docx / .xlsx 後再上傳',
      );
      return false;
    }
    if (!ACCEPT_EXT.includes(ext)) {
      message.error('僅支援 .docx / .xlsx / .xlsm 檔案');
      return false;
    }
    const parsed = parseRevisionFromFilename(file.name);
    setPendingFile(file);
    setUploadRev(parsed.revisionLabel ?? '');
    setUploadDate(parsed.revisionDate ? dayjs(parsed.revisionDate) : null);
    return false; // 阻止 antd 自行上傳
  };

  /** 上傳並識別：presign 直傳 → 同步識別建新版本（序36：舊流程呼叫不存在的 /api/files/upload） */
  const recognize = useMutation({
    mutationFn: async () => {
      const uploaded = await uploadFile(pendingFile!, {
        scope: 'form-template',
        ownerType: 'form_template',
        ownerId: item!.id,
      });
      return apiClient.post<RecognizeResponse>('/api/form-templates/recognize', {
        templateId: item!.id,
        fileId: uploaded.id,
        revisionLabel: uploadRev.trim() || null,
        revisionDate: uploadDate ? uploadDate.format('YYYY-MM-DD') : null,
      });
    },
    onSuccess: (res) => {
      if (res.method === 'fallback') {
        message.warning(
          `已建立新版本 v${res.version.version}（${res.fallbackReason ?? '規則式初稿'}），請務必逐條校對`,
          8,
        );
      } else {
        message.success(`AI 識別完成，已建立新版本 v${res.version.version}，請逐條校對`);
      }
      setPendingFile(null);
      invalidate();
      setProofreadVersion(res.version);
    },
    onError: (err) => {
      console.error('recognize form-template file failed', err);
      message.error(err instanceof Error ? `識別失敗：${err.message}` : '識別失敗，請重試', 8);
    },
  });

  // ─── 校對 ───────────────────────────────────────────────────────────────
  const [proofreadVersion, setProofreadVersion] = useState<FormTemplateVersionDto | null>(null);
  /** 版本 ID → 最新校對結果 */
  const latestProofreadByVersion = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of proofreads) if (!map.has(p.versionId)) map.set(p.versionId, p.result);
    return map;
  }, [proofreads]);
  const versionNoById = useMemo(() => new Map(versions.map((v) => [v.id, v.version])), [versions]);

  const systemUsage = detail.data?.systemUsage ?? item?.systemUsage ?? null;
  const systemKey = detail.data?.template.systemKey ?? item?.systemKey ?? null;

  return (
    <>
      <Form<FormValues>
        form={form}
        layout="vertical"
        initialValues={initialValues}
        key={`${item?.id ?? 'new'}-${currentVersionId ?? 0}`}
        onFinish={(v) => (item ? update.mutate(v) : create.mutate(v))}
        style={{ marginTop: 16 }}
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="code"
              label="表單代碼"
              rules={[{ required: true, max: 100 }]}
              extra={
                systemUsage
                  ? `此表單由系統自動使用（${systemUsage}）。代碼可自由修改，系統以內部識別鍵「${systemKey ?? item?.code ?? ''}」配對，不受影響。`
                  : undefined
              }
            >
              <Input placeholder="如 D 4-1ZHSR02-28" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="scheme" label="主要方案（顯示用，留空 = 通用）">
              <Select
                allowClear
                placeholder="通用"
                options={VERIFICATION_SCHEMES.map((s) => ({ value: s, label: s }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="name" label="表單名稱" rules={[{ required: true, max: 200 }]}>
          <Input />
        </Form.Item>

        <Form.Item name="description" label="說明">
          <Input.TextArea rows={2} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="applicableSchemes"
              label="適用方案（多選；留空 = 全部適用）"
              tooltip="稽核任務查核表單依案件方案篩選；多選時任一命中即適用"
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="全部適用"
                options={VERIFICATION_SCHEMES.map((s) => ({ value: s, label: s }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="applicableTaskTypes"
              label="適用驗證類型（多選；留空 = 全部適用）"
              tooltip="選項來自「系統設置 → 客戶字典 — 驗證類型」；案件驗證類型任一命中即適用"
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="全部適用"
                loading={options.isLoading}
                options={typeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="applicableCategories"
              label="適用驗證類別（留空或「通用」= 全部適用）"
              tooltip="依案件驗證類別匹配：如勾選「蜂蜜、加工、分裝、流通」，作物類案件不會出現此表單；「分裝流通」視同分裝＋流通"
            >
              <Select
                mode="tags"
                allowClear
                placeholder="選擇或輸入後按 Enter"
                options={categoryOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item
              name="isDefault"
              label="預設勾選"
              valuePropName="checked"
              tooltip="派案自動建立稽核任務時，自動帶入適用且預設勾選的表單"
            >
              <Switch checkedChildren="自動勾選" unCheckedChildren="手動勾選" />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="isActive" label="狀態" valuePropName="checked">
              <Switch checkedChildren="啟用" unCheckedChildren="停用" />
            </Form.Item>
          </Col>
        </Row>

        {currentVersionId && (
          <>
            <Divider titlePlacement="start" style={{ margin: '4px 0 12px' }}>
              當前版本（v{currentVer?.version ?? item?.currentVersion}）
            </Divider>
            <Row gutter={16}>
              <Col xs={12} md={6}>
                <Form.Item
                  name="revisionLabel"
                  label="版本（Rev）"
                  tooltip="表單本身的版本，如 2、1.3（顯示為 Rev2）"
                >
                  <Input prefix="Rev" maxLength={30} />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item
                  name="revisionDate"
                  label="版本日期"
                  tooltip="表單本身的改版日期（如檔名 Rev2 (20240712) → 2024-07-12），非上傳日期"
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <div style={{ fontSize: text.sm, color: color.text.secondary, lineHeight: 1.9 }}>
                  <div>
                    原始檔名：
                    {(currentVer?.sourceFileName ?? item?.sourceFileName) ? (
                      <Typography.Text copyable>
                        {currentVer?.sourceFileName ?? item?.sourceFileName}
                      </Typography.Text>
                    ) : (
                      '—'
                    )}
                  </div>
                  <div>
                    檔案上傳日期：{fmtDate(currentVer?.fileUploadedAt ?? item?.fileUploadedAt)}
                  </div>
                </div>
              </Col>
            </Row>
          </>
        )}

        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={create.isPending || update.isPending}>
            {item ? '儲存' : '建立'}
          </Button>
        </Form.Item>
      </Form>

      {item && (
        <>
          <Divider titlePlacement="start">上傳新版本</Divider>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text type="secondary" style={{ fontSize: text.sm }}>
              支援 Word（.docx）與 Excel（.xlsx / .xlsm）；舊版 .doc / .xls
              請先「另存新檔」為新格式。上傳後系統自動識別欄位建立新版本，檔名保持原樣，並自動從檔名帶入版本與版本日期。
            </Typography.Text>
            <Space wrap>
              <Upload accept=".docx,.xlsx,.xlsm" showUploadList={false} beforeUpload={onPickFile}>
                <Button icon={<UploadIcon size={14} />}>選擇檔案</Button>
              </Upload>
              {pendingFile && (
                <>
                  <Typography.Text>{pendingFile.name}</Typography.Text>
                  <Input
                    prefix="Rev"
                    placeholder="版本"
                    value={uploadRev}
                    onChange={(e) => setUploadRev(e.target.value)}
                    style={{ width: 120 }}
                  />
                  <DatePicker
                    placeholder="版本日期"
                    value={uploadDate}
                    onChange={(d) => setUploadDate(d)}
                  />
                  <Button
                    type="primary"
                    icon={<Bot size={14} />}
                    loading={recognize.isPending}
                    onClick={() => recognize.mutate()}
                  >
                    {recognize.isPending ? '識別中（約 1 分鐘）…' : '上傳並識別'}
                  </Button>
                  <Button type="text" onClick={() => setPendingFile(null)}>
                    取消
                  </Button>
                </>
              )}
            </Space>
          </Space>

          <Divider titlePlacement="start">版本紀錄</Divider>
          <Table<FormTemplateVersionDto>
            rowKey="id"
            size="small"
            loading={detail.isLoading}
            dataSource={versions}
            pagination={false}
            scroll={{ x: 900 }}
            columns={[
              {
                title: '系統版次',
                dataIndex: 'version',
                width: 90,
                render: (v: number, r) => (
                  <Space size={4}>
                    <span>v{v}</span>
                    {r.id === currentVersionId && <Tag color="green">當前</Tag>}
                  </Space>
                ),
              },
              {
                title: '版本',
                key: 'rev',
                width: 150,
                render: (_, r) =>
                  formatRevisionLabel({
                    revisionLabel: r.revisionLabel,
                    revisionDate: r.revisionDate,
                    version: r.version,
                  }),
              },
              {
                title: '原始檔名',
                dataIndex: 'sourceFileName',
                ellipsis: true,
                render: (v: string | null, r) =>
                  v ? (
                    r.docxFileId ? (
                      <Typography.Link onClick={() => void previewFile(r.docxFileId, v)}>
                        {v}
                      </Typography.Link>
                    ) : (
                      v
                    )
                  ) : (
                    '—'
                  ),
              },
              {
                title: '檔案上傳日期',
                dataIndex: 'fileUploadedAt',
                width: 120,
                render: (v: string | null) => fmtDate(v),
              },
              {
                title: '變更說明',
                dataIndex: 'changelog',
                ellipsis: true,
                render: (v: string | null) => v ?? '—',
              },
              {
                title: '校對',
                key: 'proofread',
                width: 90,
                render: (_, r) => {
                  const s = latestProofreadByVersion.get(r.id);
                  return s === 'confirmed' ? (
                    <StatusPill tone="success" size="sm">
                      已確認
                    </StatusPill>
                  ) : s === 'needs_fix' ? (
                    <StatusPill tone="danger" size="sm">
                      待修正
                    </StatusPill>
                  ) : (
                    <StatusPill tone="muted" size="sm">
                      未校對
                    </StatusPill>
                  );
                },
              },
              {
                title: '操作',
                key: 'op',
                width: 80,
                fixed: 'right',
                render: (_, r) => (
                  <Button
                    size="small"
                    icon={<FileCheck2 size={13} />}
                    onClick={() => setProofreadVersion(r)}
                  >
                    校對
                  </Button>
                ),
              },
            ]}
          />

          {proofreads.length > 0 && (
            <>
              <Divider titlePlacement="start">校對紀錄</Divider>
              <Table
                rowKey="id"
                size="small"
                dataSource={proofreads}
                pagination={{ pageSize: 5, hideOnSinglePage: true }}
                scroll={{ x: 760 }}
                columns={[
                  {
                    title: '時間',
                    dataIndex: 'createdAt',
                    width: 150,
                    render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
                  },
                  {
                    title: '版次',
                    dataIndex: 'versionId',
                    width: 70,
                    render: (v: number) => `v${versionNoById.get(v) ?? '?'}`,
                  },
                  {
                    title: '結果',
                    dataIndex: 'result',
                    width: 90,
                    render: (v: string) =>
                      v === 'confirmed' ? (
                        <Tag color="green">確認無誤</Tag>
                      ) : (
                        <Tag color="red">待修正</Tag>
                      ),
                  },
                  {
                    title: '已校對/條文數',
                    key: 'cnt',
                    width: 110,
                    render: (_, r) => `${r.checkedCount} / ${r.itemCount}`,
                  },
                  { title: '校對人', dataIndex: 'proofreadByName', width: 100 },
                  {
                    title: '通知人數',
                    dataIndex: 'notifiedCount',
                    width: 90,
                    render: (v: number) => (v > 0 ? `${v} 人` : '—'),
                  },
                  { title: '意見', dataIndex: 'notes', ellipsis: true },
                ]}
              />
            </>
          )}

          {!currentVersionId && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="此表單尚無版本，請上傳表單檔案建立第一個版本後再啟用。"
            />
          )}

          {/* key 綁版本 id：切換/新建版本時重掛 Drawer，清空上一輪的勾選、修正與備註，
              避免舊修正被重複套用成新版本或出現假進度。 */}
          <ProofreadDrawer
            key={proofreadVersion?.id ?? 'none'}
            templateId={item.id}
            templateName={item.name}
            version={proofreadVersion}
            onClose={() => setProofreadVersion(null)}
          />
        </>
      )}
    </>
  );
}
